import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Activity, DollarSign, Bell, Cpu, Clapperboard, UserPlus, CreditCard,
  SlidersHorizontal, LayoutTemplate, ListChecks, ArrowLeft, Radio, PlaySquare, Receipt, ShieldAlert, Palette, Puzzle, Mail,
} from "lucide-react";
import { useControlCenter, useSetSystemStatus } from "@/hooks/useAdmin";
import { UsersPanel, PricingSettings, LandingContentManager, AlertsLog, PublicPlaylistManager, TransactionsPanel, ModerationLog, BrandingManager } from "@/pages/Admin";
import { PluginManager } from "@/components/admin/PluginManager";
import { WaitlistPanel } from "@/components/admin/WaitlistPanel";
import { apiError } from "@/lib/api";

const STATUS_MAP = {
  green: { dot: "bg-emerald-500", ring: "ring-emerald-400/50", text: "text-emerald-600", label: "Operational" },
  yellow: { dot: "bg-amber-500", ring: "ring-amber-400/50", text: "text-amber-600", label: "Degraded" },
  red: { dot: "bg-rose-500", ring: "ring-rose-400/50", text: "text-rose-600", label: "Outage" },
};
const STATUS_CYCLE = { green: "yellow", yellow: "red", red: "green" };

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Card({ children, testid, accent = false }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border ${accent ? "border-blue-300 bg-blue-50" : "border-blue-200 bg-white"} shadow-sm p-4 flex flex-col justify-between min-h-0`}
    >
      {children}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rounded-xl border border-blue-200 bg-white p-4 animate-pulse">
      <div className="h-4 w-4 rounded bg-blue-100 mb-4" />
      <div className="h-7 w-20 rounded bg-blue-100 mb-2" />
      <div className="h-3 w-24 rounded bg-blue-100" />
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone = "text-slate-900", testid }) {
  return (
    <Card testid={testid}>
      <Icon className="h-4 w-4 text-blue-500" />
      <div>
        <div className={`font-display text-3xl font-extrabold leading-none tracking-tight ${tone}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</span>
          {sub && <span className="text-[10px] text-slate-400">{sub}</span>}
        </div>
      </div>
    </Card>
  );
}

const PANELS = [
  { key: "users", label: "Users", icon: Users, testid: "qa-users" },
  { key: "waitlist", label: "Waitlist", icon: Mail, testid: "qa-waitlist" },
  { key: "featured", label: "Featured", icon: PlaySquare, testid: "qa-featured" },
  { key: "transactions", label: "Transactions", icon: Receipt, testid: "qa-transactions" },
  { key: "moderation", label: "Moderation", icon: ShieldAlert, testid: "qa-moderation" },
  { key: "landing", label: "Landing", icon: LayoutTemplate, testid: "qa-landing" },
  { key: "branding", label: "Branding", icon: Palette, testid: "qa-branding" },
  { key: "plugins", label: "Plugins", icon: Puzzle, testid: "qa-plugins" },
  { key: "pricing", label: "Pricing", icon: SlidersHorizontal, testid: "qa-pricing" },
  { key: "alerts", label: "Alerts", icon: ListChecks, testid: "qa-alerts" },
];

const PANEL_TITLES = {
  users: "User Management",
  waitlist: "Waitlist & Invites",
  featured: "Watch & Learn Free — Featured Courses",
  transactions: "Transactions & Refunds",
  moderation: "Moderation — Blocked Topics",
  landing: "Landing Page Content",
  branding: "Branding & Logo",
  plugins: "Dojo Core — Plugin Switchboard",
  pricing: "Pricing & Coin Settings",
  alerts: "Alerts Log",
};

export default function ControlCenter() {
  const navigate = useNavigate();
  const [panel, setPanel] = useState("users");
  const { data, isLoading, dataUpdatedAt } = useControlCenter();
  const setStatus = useSetSystemStatus({
    onError: (e) => toast.error(apiError(e)),
  });

  const status = STATUS_MAP[data?.system_status || "green"];
  const gen = data?.course_generations || { today: 0, yesterday: 0 };
  const genDelta = gen.today - gen.yesterday;

  return (
    <div className="h-screen flex flex-col bg-slate-50 text-slate-900 overflow-hidden font-mono">
      {/* Top bar */}
      <header className="h-14 shrink-0 flex items-center justify-between px-5 bg-primary text-white">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} data-testid="cc-back" className="text-white/70 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Radio className="h-4 w-4 text-white" />
          <h1 className="font-display font-extrabold text-sm uppercase tracking-[0.2em]">Starfleet Control Center</h1>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-white/70">
          <span className="hidden sm:inline">Updated {dataUpdatedAt ? relTime(new Date(dataUpdatedAt).toISOString()) : "—"} · auto 30s</span>
          <button onClick={() => navigate("/admin/console")} data-testid="cc-console" className="rounded-full border border-white/40 px-3 py-1 hover:bg-white/10 text-white">
            Full Console
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 p-4 grid grid-rows-[auto_auto_auto_1fr] gap-3">
        {/* Row 1 — live status */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-row-live">
          {isLoading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} />) : (
            <>
              <Metric icon={Users} label="Users Online" value={data.users_online} testid="metric-users-online" tone="text-primary" />
              <Card testid="metric-system-status" accent>
                <Activity className="h-4 w-4 text-blue-500" />
                <div>
                  <button
                    data-testid="system-status-toggle"
                    onClick={() => setStatus.mutate(STATUS_CYCLE[data.system_status || "green"])}
                    disabled={setStatus.isPending}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 ring-2 ${status.ring} bg-white`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${status.dot} animate-pulse`} />
                    <span className={`text-sm font-bold ${status.text}`}>{status.label}</span>
                  </button>
                  <div className="mt-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500">System Status · click to change</div>
                </div>
              </Card>
              <Metric icon={DollarSign} label="Today's Revenue" value={`$${data.todays_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} testid="metric-todays-revenue" />
              <Metric icon={Bell} label="Active Alerts" value={data.active_alerts}
                tone={data.active_alerts > 0 ? "text-rose-600" : "text-slate-900"} testid="metric-active-alerts" />
            </>
          )}
        </section>

        {/* Row 2 — operational metrics */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-row-ops">
          {isLoading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} />) : (
            <>
              <Metric icon={Cpu} label="LLM Calls · 24h" value={data.llm_calls_24h}
                sub={`~$${data.llm_credit_cost_24h.toFixed(2)}`} testid="metric-llm-calls" />
              <Metric icon={Clapperboard} label="Generations Today" value={gen.today}
                sub={`${genDelta >= 0 ? "+" : ""}${genDelta} vs yest (${gen.yesterday})`}
                tone={genDelta >= 0 ? "text-emerald-600" : "text-amber-600"} testid="metric-generations" />
              <Metric icon={UserPlus} label="New Signups Today" value={data.new_signups_today} testid="metric-signups" />
              <Metric icon={CreditCard} label="Failed Transactions" value={data.failed_transactions}
                tone={data.failed_transactions > 0 ? "text-rose-600" : "text-slate-900"} testid="metric-failed-txns" />
            </>
          )}
        </section>

        {/* Row 3 — panel selectors */}
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="cc-row-actions">
          {PANELS.map((p) => {
            const active = panel === p.key;
            return (
              <button
                key={p.key}
                data-testid={p.testid}
                onClick={() => setPanel(p.key)}
                aria-pressed={active}
                className={`group flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                  active
                    ? "border-primary bg-primary text-white shadow-sm"
                    : "border-blue-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50"
                }`}
              >
                <p.icon className={`h-4 w-4 ${active ? "text-white" : "text-blue-500"}`} /> {p.label}
              </button>
            );
          })}
        </section>

        {/* Bottom — inline panel content (working area) */}
        <section className="flex flex-col min-h-0 rounded-xl border border-blue-200 bg-white shadow-sm overflow-hidden" data-testid="cc-panel-region">
          <div className="shrink-0 flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-200">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-bold">{PANEL_TITLES[panel]}</span>
            <button onClick={() => navigate("/admin/console")} className="text-[11px] text-slate-500 hover:text-primary">
              Open full console →
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto bg-white text-foreground p-5" data-testid="cc-panel-content">
            {panel === "users" && <UsersPanel />}
            {panel === "waitlist" && <WaitlistPanel />}
            {panel === "featured" && <PublicPlaylistManager />}
            {panel === "transactions" && <TransactionsPanel />}
            {panel === "moderation" && <ModerationLog />}
            {panel === "landing" && <LandingContentManager />}
            {panel === "branding" && <BrandingManager />}
            {panel === "plugins" && <PluginManager />}
            {panel === "pricing" && <PricingSettings />}
            {panel === "alerts" && <AlertsLog />}
          </div>
        </section>
      </main>
    </div>
  );
}
